/**
 * Shared WOPI conflict detection and resolution hook.
 *
 * Used by all WOPI-based editors (Collabora) (Excel, Doc, Slides, PDF).
 * Listens for WOPI conflict/dirty/discard/resolve custom events,
 * manages conflict state, and provides discard/overwrite handlers.
 */

import { officeVersionsPath } from './office-versions.js'
import { useState, useEffect, useCallback, useRef } from 'react'
import { request, pinnedBatch } from '@papan/api-client'
import type { ConflictView } from './ConflictBanner.js'

interface UseWopiConflictOptions {
  fileId: string
  onSaveRef?: React.MutableRefObject<(() => void) | null>
  onDiscardRef?: React.MutableRefObject<(() => void) | null>
  refreshRef?: React.MutableRefObject<(() => Promise<boolean>) | null>
  /** THIS editor instance's "my session's save landed" hook (OnlyOffice): the modifier calls
   *  it for a save from its OWN key; Keep mine arms it while it waits. Instance-scoped — never
   *  a window event another editor of the same file (another deck, another key) could fire. */
  sessionSavedRef?: React.MutableRefObject<(() => void) | null>
  onDirtyChange?: (dirty: boolean) => void
  onConflictChange?: (conflicted: boolean) => void
  /**
   * When another tab discards, should THIS (non-initiating) tab reload too?
   *
   * Collabora: NO — co-editors share one coolwsd kit, so the initiator's single
   * revert propagates over the shared session (and a second reload tears the shared
   * broker down mid-session). OnlyOffice: YES — a discard bumps discardCount → a new
   * `document.key`, so every other tab stays in the OLD co-editing session on stale
   * content until it rotates onto the new key by reloading. Defaults to false
   * (Collabora); OnlyOfficeEditor opts in.
   */
  reloadOnRemoteDiscard?: boolean
  /** Opened in the SHARE VIEW — the version list comes from the share session's route. */
  shareView?: boolean
}

interface UseWopiConflictReturn {
  isDirty: boolean
  isConflicted: boolean
  conflictView: ConflictView
  setConflictView: (view: ConflictView) => void
  conflictGeneration: number
  editorGeneration: number
  showSavedVersion: boolean
  handleDirtyChange: (dirty: boolean) => void
  handleDiscard: () => Promise<void>
  handleOverwrite: () => Promise<void>
}

export function useWopiConflict({
  fileId,
  onSaveRef,
  onDiscardRef,
  refreshRef,
  sessionSavedRef,
  onDirtyChange,
  onConflictChange,
  reloadOnRemoteDiscard = false,
  shareView = false,
}: UseWopiConflictOptions): UseWopiConflictReturn {
  const [isDirty, setIsDirty] = useState(false)
  const isDirtyRef = useRef(false)
  const [isConflicted, setIsConflicted] = useState(false)
  const isConflictedRef = useRef(false)
  const [conflictView, setConflictView] = useState<ConflictView>('mine')
  const [editorGeneration, setEditorGeneration] = useState(0)
  const [conflictGeneration, setConflictGeneration] = useState(0)
  const refreshingRef = useRef(false)
  const refreshCooldownRef = useRef(false)
  // A change that arrived while a reload was in flight or during the post-reload
  // cooldown. Set instead of dropping the event, so a trailing reload fires when
  // the window clears — otherwise the LAST edit of a rapid burst is swallowed and
  // Collabora stays stuck one version behind (#582).
  const pendingRefreshRef = useRef(false)

  // Clear the post-reload cooldown, and if a change arrived during it (or during a
  // discard's suppression window), reload ONE more time to land on the latest.
  // SHARED by the refresh path and handleDiscard so whichever cooldown clears
  // first CONSUMES the pending by dispatching a synthetic event — it must never
  // just reset the shared refs, or a stray discard timer could race away a real
  // trailing reload owed to a concurrent refresh cycle (and dropping it is the
  // #582 stuck-view symptom). Idempotent: a second clear finds pending already
  // false and no-ops, so overlapping cooldown timers can't double-reload.
  const clearCooldown = useCallback(() => {
    refreshCooldownRef.current = false
    if (pendingRefreshRef.current) {
      pendingRefreshRef.current = false
      window.dispatchEvent(new CustomEvent('papan:oo-conflict', { detail: { fileId } }))
    }
  }, [fileId])

  const handleDirtyChange = useCallback((dirty: boolean) => {
    isDirtyRef.current = dirty
    setIsDirty(dirty)
  }, [])

  // Relay dirty state to parent
  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  // Relay conflict state to parent
  useEffect(() => {
    isConflictedRef.current = isConflicted
    onConflictChange?.(isConflicted)
  }, [isConflicted, onConflictChange])

  // Reset conflict view when conflict resolves
  useEffect(() => {
    if (!isConflicted) setConflictView('mine')
  }, [isConflicted])

  // Listen for WOPI conflict events
  useEffect(() => {
    const handler = async (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.fileId !== fileId) return

      // The rule: a version change reloads the doc (with the sheet-change check) UNLESS
      // this tab has unsaved edits — then we must NOT auto-reload, or we clobber the
      // user's work. So the DIRTY check comes FIRST, and it wins even over a structural
      // change: a dirty tab goes to conflict mode (the "your changes / saved version"
      // banner) regardless of sheetsChanged, and the user resolves via keep-mine/discard
      // (discard's own reload is sheet-aware). Client-side dirty (isDirtyRef) tracks
      // actual editor modifications; a stale server flag would mis-fire a phantom
      // conflict.
      // `draftConflict` (I27, D4): the editor OPENED on a draft recorded against a version
      // the file has since moved past (GET /api/wopi/drafts/:id/status → conflict) — unsaved
      // work by definition, flagged explicitly because the dirty state it just set has not
      // re-rendered into isDirtyRef yet.
      const clientDirty = isDirtyRef.current || isConflictedRef.current || detail?.draftConflict === true
      if (clientDirty) {
        setIsConflicted(true)
        isConflictedRef.current = true
        setConflictGeneration(g => g + 1)
        return
      }

      // Clean tab → safe to reload in place (no unsaved work to lose). A STRUCTURAL change
      // (sheet/slide add/remove/rename/reorder) needs a FULL remount to rebuild tabs;
      // checked before the cooldown guard so a burst ending in a structural change is
      // never swallowed (a soft-only reload would leave a stale tab, §5.C / #582).
      if (detail?.sheetsChanged) {
        setEditorGeneration(g => g + 1)
        return
      }

      // A reload is in flight or the post-reload cooldown is still armed. Don't drop this
      // event — the burst's LAST change would be lost and the editor would stay a version
      // behind (#582). Remember it; clearCooldown fires the trailing reload.
      if (refreshCooldownRef.current || refreshingRef.current) {
        pendingRefreshRef.current = true
        return
      }

      // Content-only change on a clean tab → fast soft refresh (no remount).
      refreshingRef.current = true
      refreshCooldownRef.current = true
      for (let i = 0; i < 10; i++) {
        await new Promise(r => setTimeout(r, 300))
        const refreshed = await refreshRef?.current?.()
        if (refreshed) {
          refreshingRef.current = false
          setTimeout(clearCooldown, 3000)
          return
        }
      }
      setEditorGeneration(g => g + 1)
      refreshingRef.current = false
      clearCooldown()
    }

    window.addEventListener('papan:oo-conflict', handler)
    return () => window.removeEventListener('papan:oo-conflict', handler)
  }, [fileId, refreshRef, clearCooldown])

  // Listen for dirty/discard/resolve broadcasts
  useEffect(() => {
    const dirtyHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.fileId !== fileId) return
      if (detail.isDirty === false) {
        isDirtyRef.current = false
        setIsDirty(false)
      }
    }

    const discardHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.fileId !== fileId) return
      isDirtyRef.current = false
      setIsDirty(false)
      setIsConflicted(false)
      isConflictedRef.current = false

      // The engines differ here, and it is the whole point of the flag.
      //
      // COLLABORA (reloadOnRemoteDiscard=false): revert IN PLACE, only the INITIATOR
      // reloads. A discard bumps the discard generation, which the server folds into
      // the WOPI `Version` (CheckFileInfo), so coolwsd re-fetches the clean committed
      // content on Reload_From_Storage. The WOPISrc is UNCHANGED, so every co-editor
      // stays on the ONE shared coolwsd kit — the initiator's single revert reverts
      // the one document for everyone and propagates to the other views over the
      // shared session, WITHOUT their own reload. Reloading here too would be actively
      // harmful: two near-simultaneous Reload_From_Storage calls tear the shared broker
      // down while sessions are still attached ("Destroying DocumentBroker … with 2
      // references"), interrupting every co-editor — including the one who never
      // clicked Discard. Clearing the local indicators (above) is all a non-initiator
      // needs.
      //
      // ONLYOFFICE (reloadOnRemoteDiscard=true): there is NO shared kit. A discard
      // bumps discardCount → a NEW document.key, and the initiator's own discard()
      // rotates ITSELF onto the clean key; every OTHER tab stays in the OLD co-editing
      // session on the stale (dirty) content — worse, if one of them autosaves it
      // clobbers the discard. So every tab must reload to rotate onto the new key. The
      // modifier's refresh() re-mints the token (picking up the bumped discardCount)
      // and its `oldKey === docKey` guard makes this idempotent: the initiator sees the
      // key it already rotated to and no-ops, other tabs see the new key and reload
      // clean. Fire-and-forget — the reset listener already cleared the badge.
      if (reloadOnRemoteDiscard) {
        // If the revert changes the sheet/slide set, a soft reload would leave the removed
        // sheet's tab behind (§5.C) — bump the generation to force a full remount that
        // rebuilds the tabs. Otherwise take the fast in-place refresh. (The server computes
        // sheetsChanged by comparing the discarding editor's live sheet names to the
        // committed file's names, and carries it on the broadcast — see wopi-routes /discard.)
        if (detail.sheetsChanged) {
          setEditorGeneration((g) => g + 1)
        } else {
          void refreshRef?.current?.()
        }
      }
    }

    const resolveHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.fileId !== fileId) return
      setIsConflicted(false)
      isConflictedRef.current = false
    }

    window.addEventListener('papan:oo-dirty', dirtyHandler)
    window.addEventListener('papan:oo-discard', discardHandler)
    window.addEventListener('papan:oo-resolve', resolveHandler)
    return () => {
      window.removeEventListener('papan:oo-dirty', dirtyHandler)
      window.removeEventListener('papan:oo-discard', discardHandler)
      window.removeEventListener('papan:oo-resolve', resolveHandler)
    }
  }, [fileId, reloadOnRemoteDiscard, refreshRef])

  const handleDiscard = useCallback(async () => {
    // Suppress the oo-conflict handler around our own discard so a conflict
    // event can't kick off a stale-broker Reload_From_Storage while discard()
    // is re-initing. (The oo-discard remount is authoritative regardless — this
    // only avoids a redundant reload racing it.)
    refreshCooldownRef.current = true
    pendingRefreshRef.current = false
    isDirtyRef.current = false
    setIsDirty(false)
    setIsConflicted(false)
    isConflictedRef.current = false
    try {
      if (onDiscardRef?.current) {
        await onDiscardRef.current()
      }
    } finally {
      // Release the suppression window through the SHARED clearCooldown, not a
      // bare ref reset: if an AI edit landed during this window it set
      // pendingRefreshRef, and clearCooldown fires that one trailing reload to
      // land on the latest. Sharing the consumer is also what stops this timer
      // racing away a pending owed to a concurrent normal refresh cycle.
      setTimeout(clearCooldown, 3000)
    }
  }, [onDiscardRef, clearCooldown])

  const handleOverwrite = useCallback(async () => {
    // Save FIRST, and dismiss the conflict ONLY once the edit has actually PERSISTED.
    // The old order (resolve-conflict → save) broadcast oo-resolve — which dismisses the
    // banner + clears the badge — BEFORE saving, so a forcesave that ACK'd but assembled
    // nothing (OnlyOffice/EuroOffice error 4 on a key it no longer has an active session
    // for) cleared the conflict on an UNSAVED doc → false "saved", edits lost on reload
    // (#617). A forcesave ACK is not persistence: the write lands via an async status-6
    // callback, so poll the server dirty flag until it clears (saved) or times out.
    // Persistence signal = a NEW onlyoffice-authored VERSION actually written, NOT the
    // dirty flag clearing. A status-4 "closed, no changes" callback clears the flag
    // during session churn WITHOUT any save (#617), so "flag cleared" is a false
    // positive; and an agent version is not our save. Snapshot the newest onlyoffice
    // version time before saving, then poll for a newer one (the write lands async).
    const newestOoTs = (list: any): string => {
      const arr = Array.isArray(list) ? list : (list?.versions || [])
      for (const x of arr) {
        const by = String(x.author ?? x.source ?? '').toLowerCase()
        if (by.includes('onlyoffice')) return String(x.createdAt ?? x.created_at ?? '')
      }
      return ''
    }
    const versionsUrl = officeVersionsPath(fileId, shareView)
    // A `.shared`/`.chain` recipient cannot read the version list by its compound id (403)
    // — Keep mine then has two other persistence signals (I27, D4): the server confirming a
    // save that landed FROM THIS SESSION (`sessionSavedRef` — the editor instance's own key), and the draft status coming back clean
    // (no flag, no conflict). The latter is safe HERE: the "flag cleared without a save"
    // path (#617) is a status-4 close, which only comes once every editor has closed — this
    // one is open, waiting on its own save.
    let versionsReadable = true
    // #1316 review round 2 (owner, 2026-10-28): the Keep mine the person pressed FINISHES as the identity that pressed it
    // — its persistence checks and the resolve go as it, never as the next session (the editor tab remounts on its own)
    // (taken while its session is live, so it never refuses: an identity that names no email is `{}` — its requests stay
    // bound to the session, and one dropped at its end reads as "not persisted")
    const asStarter = pinnedBatch().opts()
    // Only a REFUSAL (403 — a compound id) means "this id has no readable version list"; a
    // transient failure (5xx, network, 429) says nothing, and must not swap the proof of
    // persistence for the weaker status signal (review round 10).
    const before = await request(versionsUrl, asStarter).then(newestOoTs).catch((err: { status?: number }) => {
      if (err?.status === 403) versionsReadable = false
      return ''
    })
    // A FILE-level `.shared`/`.chain` pointer's own id reads the POINTER row's versions (200,
    // never the source's) — no newer version ever shows there; the server says which ids
    // those are. Only where the version list cannot confirm does the draft status decide:
    // for a native file a clean status is NOT proof (the flag is per file and another
    // session's clean close clears it while this save is still in flight, #617).
    const viaPointer = versionsReadable
      ? await request<{ viaPointer?: boolean }>(`/api/wopi/drafts/${fileId}/status`, asStarter).then((st) => st?.viaPointer === true).catch(() => false)
      : true
    let savedSignal = false
    if (sessionSavedRef) sessionSavedRef.current = () => { savedSignal = true }
    let persisted = false
    try {
      if (onSaveRef?.current) {
        await onSaveRef.current()
      }
      for (let i = 0; i < 10; i++) {
        if (savedSignal) { persisted = true; break }
        if (versionsReadable) {
          const now = await request(versionsUrl, asStarter).then(newestOoTs).catch(() => before)
          if (now && now > before) { persisted = true; break }
        }
        if (viaPointer) {
          const st = await request<{ isDirty: boolean; conflict?: boolean }>(`/api/wopi/drafts/${fileId}/status`, asStarter).catch(() => null)
          if (st && !st.isDirty && !st.conflict) { persisted = true; break }
        }
        await new Promise((r) => setTimeout(r, 400))
      }
    } finally {
      if (sessionSavedRef) sessionSavedRef.current = null
    }
    if (!persisted) {
      // Not saved — keep the conflict banner + dirty badge so the user can retry. A retry
      // re-issues the forcesave; forcesave (not periodic assembly — that's off) is the only
      // thing that commits, so the retry is what makes progress.
      console.warn(`[OnlyOffice] overwrite did not persist for ${fileId} — keeping conflict (forcesave assembled nothing)`)
      return
    }
    // Persisted → dismiss the conflict across all tabs (oo-resolve clears isConflicted).
    await request('/api/wopi/resolve-conflict', {
      method: 'POST',
      body: JSON.stringify({ fileId }),
      headers: { 'Content-Type': 'application/json' },
      ...asStarter,
    }).catch(() => {})
  }, [fileId, onSaveRef, sessionSavedRef])

  return {
    isDirty,
    isConflicted,
    conflictView,
    setConflictView,
    conflictGeneration,
    editorGeneration,
    showSavedVersion: isConflicted && conflictView === 'saved',
    handleDirtyChange,
    handleDiscard,
    handleOverwrite,
  }
}
